import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { isPassthrough, resolveStorageRef } from './storageRef';

/**
 * Signed URLs voor Supabase Storage.
 *
 * `getPublicUrl()` levert alleen een werkende link bij een PUBLIEKE bucket: iedereen met de
 * URL komt er dan bij, zonder inloggen en zonder RLS. Voor bedrijfsgevoelige inhoud
 * (offertes, facturen, opnames, inkoopfacturen) hoort de bucket op `public = false` te staan
 * en gebruik je een signed URL met een korte geldigheidsduur.
 *
 * Gebruik deze helpers voor alles wat uit een bucket komt die privé is of privé wordt. Sla
 * de uitkomst NOOIT op in de database: een signed URL verloopt. Bewaar altijd het pad (of de
 * historisch opgeslagen publieke URL) en teken opnieuw bij het tonen.
 *
 * Alle helpers accepteren zowel `(bucket, pad)` als een absolute publieke storage-URL — in
 * de database staat namelijk allebei (zie `storageRef.ts`). Externe URL's en data-URI's
 * komen ongewijzigd terug, zodat een `<img src>` daarop blijft werken.
 */

/** Standaard geldigheidsduur: lang genoeg om een pagina te tonen, kort genoeg om te delen zinloos te maken. */
export const SIGNED_URL_TTL_SECONDS = 60 * 10;

/** Marge zodat een net uitgedeelde URL niet halverwege het laden alsnog verloopt. */
const CACHE_MARGIN_SECONDS = 60;

const cache = new Map<string, { url: string; expiresAt: number }>();

/** Alleen voor tests/debug: leegt de in-memory cache met signed URLs. */
export function clearSignedUrlCache(): void {
  cache.clear();
}

/**
 * Tekent een storage-verwijzing en geeft een tijdelijke URL terug. Resultaten worden in
 * memory gecachet tot kort voor het verlopen, zodat een lijst met 50 thumbnails niet 50 keer
 * opnieuw tekent bij elke render.
 *
 * Aanroepvormen:
 *   getSignedUrl('photos', foto.file_path)   // bucket + pad (of een opgeslagen URL)
 *   getSignedUrl(rij.foto_url)               // opgeslagen absolute publieke URL
 */
export async function getSignedUrl(
  bucketOrUrl: string | null | undefined,
  path?: string | null,
  ttlSeconds: number = SIGNED_URL_TTL_SECONDS,
): Promise<string | null> {
  const ref = resolveStorageRef(bucketOrUrl, path);
  if (!ref) return null;
  if (isPassthrough(ref)) return ref.passthrough;

  const key = `${ref.bucket}::${ref.path}::${ttlSeconds}`;
  const hit = cache.get(key);
  if (hit && hit.expiresAt > Date.now()) return hit.url;

  const { data, error } = await supabase.storage
    .from(ref.bucket)
    .createSignedUrl(ref.path, ttlSeconds);
  if (error || !data?.signedUrl) return null;

  cache.set(key, {
    url: data.signedUrl,
    expiresAt: Date.now() + (ttlSeconds - CACHE_MARGIN_SECONDS) * 1000,
  });
  return data.signedUrl;
}

/**
 * Tekent meerdere verwijzingen in één keer; geeft een map terug met de ORIGINELE waarde als
 * sleutel (het pad of de opgeslagen URL), zodat de aanroeper zijn eigen veld kan opzoeken.
 */
export async function getSignedUrls(
  bucket: string | null | undefined,
  paths: (string | null | undefined)[],
  ttlSeconds: number = SIGNED_URL_TTL_SECONDS,
): Promise<Record<string, string>> {
  const result: Record<string, string> = {};
  const uniek = Array.from(new Set(paths.filter((p): p is string => Boolean(p))));
  await Promise.all(
    uniek.map(async (path) => {
      const url = await getSignedUrl(bucket, path, ttlSeconds);
      if (url) result[path] = url;
    }),
  );
  return result;
}

/**
 * Verwijdert het onderliggende bestand van een storage-verwijzing.
 *
 * Accepteert dezelfde vormen als de teken-helpers, want in de database staat naast een kaal
 * pad ook de historisch opgeslagen publieke URL. Zelf splitsen op `/<bucket>/` gaat bij een
 * kaal pad mis: dan blijft het bestand achter terwijl de rij al weg is.
 *
 * Geeft `false` terug als er niets te verwijderen viel (externe URL, data-URI, lege waarde)
 * of als storage het weigerde. Het verwijderen van de databaserij is leidend; een
 * achtergebleven bestand is rommel, geen fout die de gebruiker moet zien.
 */
export async function removeStorageObject(
  bucketOrUrl: string | null | undefined,
  path?: string | null,
): Promise<boolean> {
  const ref = resolveStorageRef(bucketOrUrl, path);
  if (!ref || isPassthrough(ref)) return false;

  const { error } = await supabase.storage.from(ref.bucket).remove([ref.path]);
  if (error) {
    console.error('Storage-object verwijderen mislukt:', ref.bucket, ref.path, error.message);
    return false;
  }
  return true;
}

/**
 * React-hook: geeft een signed URL voor een storage-verwijzing, of `null` zolang die er nog
 * niet is. Handig voor `<img src={...}>` waar het pad pas na het laden van data bekend is.
 */
export function useSignedUrl(
  bucketOrUrl: string | null | undefined,
  path?: string | null,
  ttlSeconds: number = SIGNED_URL_TTL_SECONDS,
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getSignedUrl(bucketOrUrl, path, ttlSeconds).then((signed) => {
      if (active) setUrl(signed);
    });
    return () => {
      active = false;
    };
  }, [bucketOrUrl, path, ttlSeconds]);

  return url;
}

/** Hook-variant voor een lijst verwijzingen; geeft een map pad -> URL terug. */
export function useSignedUrls(
  bucket: string | null | undefined,
  paths: (string | null | undefined)[],
  ttlSeconds: number = SIGNED_URL_TTL_SECONDS,
): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  // `paths` is elke render een nieuwe array-referentie; de samengevoegde sleutel niet.
  const key = paths.filter((p): p is string => Boolean(p)).join('|');

  useEffect(() => {
    let active = true;
    void getSignedUrls(bucket, key ? key.split('|') : [], ttlSeconds).then((map) => {
      if (active) setUrls(map);
    });
    return () => {
      active = false;
    };
  }, [bucket, key, ttlSeconds]);

  return urls;
}
